import type { ReactNode } from 'react';

// "Nice" tick values (as most charting libs do) so labels land on round
// numbers (0, 25, 50...) instead of whatever fraction `max / count` happens
// to produce.
function niceStep(rough: number): number {
  const exp = Math.floor(Math.log10(rough || 1));
  const base = Math.pow(10, exp);
  const frac = rough / base;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * base;
}

// Ticks from top (≈max) to bottom (0) - matches the top-to-bottom label
// column and gridlines below.
export function ticksFor(max: number, count = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const step = niceStep(max / count) || 1;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = top; v >= 0; v -= step) ticks.push(v);
  return ticks;
}

function formatTick(v: number): string {
  if (Math.abs(v) >= 1000) return `${+(v / 1000).toFixed(1)}k`;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

// Wraps a chart body (line/bar/grouped/stacked) with a numbered Y axis and
// gray dotted horizontal gridlines - shared so every chart type gets the
// same look without duplicating axis math per component.
export function ChartAxis({ max, height = 180, children }: { max: number; height?: number; children: ReactNode }) {
  const ticks = ticksFor(max);
  return (
    <div className="flex gap-2 w-full" style={{ height }}>
      <div className="flex flex-col justify-between text-[10px] text-black/40 dark:text-white/40 text-right shrink-0 tabular-nums">
        {ticks.map((t, i) => (
          <span key={i}>{formatTick(t)}</span>
        ))}
      </div>
      <div className="relative flex-1 h-full">
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {ticks.map((_, i) => (
            <div key={i} className="border-t border-dotted border-black/15 dark:border-white/15" />
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}
